/**
 * Audio Feedback Service
 * Provides audio cues and text-to-speech functionality
 */

interface AudioFeedbackOptions {
  volume?: number;
  language?: string;
  rate?: number;
  pitch?: number;
}

class AudioFeedbackService {
  private audioContext: AudioContext | null = null;
  private soundEnabled: boolean = true;
  private currentUtterance: SpeechSynthesisUtterance | null = null;

  constructor() {
    this.loadPreferences();
  }

  private loadPreferences(): void {
    try {
      const stored = localStorage.getItem('audio_preferences');
      if (stored) {
        const prefs = JSON.parse(stored);
        this.soundEnabled = prefs.soundEnabled ?? true;
      }
    } catch {
      // Use defaults
    }
  }

  private savePreferences(): void {
    localStorage.setItem('audio_preferences', JSON.stringify({
      soundEnabled: this.soundEnabled,
    }));
  }

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  setSoundEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
    this.savePreferences();
    if (!enabled) {
      this.stopSpeaking();
    }
  }

  isSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  // Play a tone for feedback
  playTone(frequency: number = 440, duration: number = 0.1, type: OscillatorType = 'sine'): void {
    if (!this.soundEnabled) return;

    try {
      const ctx = this.getAudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
      
      gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (error) {
      console.warn('Audio feedback failed:', error);
    }
  }

  // Success sound
  playSuccess(): void {
    this.playTone(880, 0.1);
    setTimeout(() => this.playTone(1108, 0.15), 100);
  }

  // Error sound
  playError(): void {
    this.playTone(220, 0.15, 'square');
  }

  // Click/tap sound
  playClick(): void {
    this.playTone(600, 0.05);
  }

  // Recording start
  playRecordingStart(): void {
    this.playTone(440, 0.1);
    setTimeout(() => this.playTone(554, 0.1), 100);
    setTimeout(() => this.playTone(659, 0.1), 200);
  }

  // Recording stop
  playRecordingStop(): void {
    this.playTone(659, 0.1);
    setTimeout(() => this.playTone(554, 0.1), 100);
    setTimeout(() => this.playTone(440, 0.15), 200);
  }

  // Text-to-speech
  speak(text: string, options: AudioFeedbackOptions = {}): Promise<void> {
    if (!this.soundEnabled) return Promise.resolve();

    return new Promise((resolve, reject) => {
      if (!('speechSynthesis' in window)) {
        reject(new Error('Speech synthesis not supported'));
        return;
      }

      // Cancel any ongoing speech
      this.stopSpeaking();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.volume = options.volume ?? 1;
      utterance.rate = options.rate ?? 0.9;
      utterance.pitch = options.pitch ?? 1;
      utterance.lang = options.language ?? 'en-US';

      utterance.onend = () => {
        this.currentUtterance = null;
        resolve();
      };

      utterance.onerror = (event) => {
        this.currentUtterance = null;
        reject(new Error(event.error));
      };

      this.currentUtterance = utterance;
      window.speechSynthesis.speak(utterance);
    });
  }

  stopSpeaking(): void {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    this.currentUtterance = null;
  }

  isSpeaking(): boolean {
    return 'speechSynthesis' in window && window.speechSynthesis.speaking;
  }

  // Get available voices
  getVoices(): SpeechSynthesisVoice[] {
    if (!('speechSynthesis' in window)) return [];
    return window.speechSynthesis.getVoices();
  }

  // Haptic feedback
  vibrate(pattern: number | number[] = 10): void {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }

  vibrateSuccess(): void {
    this.vibrate([10, 50, 10]);
  }

  vibrateError(): void {
    this.vibrate([50, 50, 50]);
  }

  vibrateClick(): void {
    this.vibrate(10);
  }
}

export const audioFeedback = new AudioFeedbackService();
